/**
 * useDocsPages — shared arbi-root loader for the published help corpus (Day 100b.2).
 *
 * Fetches the full published docs_pages set once (all columns, is_published=true,
 * ordered by title) via the arbi-root supabase client, mirroring the arbi-docs
 * HelpDocs mount-effect fetch but as a reusable arbi-root unit. arbi-root had no
 * production docs_pages consumer before this; the arbi-docs fetch is cross-app and
 * not reusable. Consumed by the Help overlay Docs tab (render + browse) and — via
 * the rows it returns — the client-side corpus search (src/lib/docsSearch.ts).
 * Read-only over the existing docs_pages_select_published RLS: no new query
 * capability, no migration.
 */
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

export type DocsPage = Database['public']['Tables']['docs_pages']['Row'];

export interface UseDocsPagesResult {
  rows: DocsPage[];
  loading: boolean;
}

export function useDocsPages(): UseDocsPagesResult {
  const [rows, setRows] = useState<DocsPage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data } = await supabase
        .from('docs_pages')
        .select('*')
        .eq('is_published', true)
        .order('title', { ascending: true });
      if (!active) return;
      setRows(data ?? []);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return { rows, loading };
}
